<template>
    <div>
        <div v-if="!loading">
            <div v-if="!!site">
                <div v-if="isShopify()" style="max-height: 300px">
                    <div v-if="isShopify()" class="d-flex justify-content-center align-items-center flex-column">
                        <ShopifyIcon style="width: 100px; height: 100px" />
                        <h3>{{ $t("message.shopifyManagesInvoices") }}</h3>
                    </div>
                </div>
                <div id="archive-billing" v-else>
                    <a-table
                        :columns="cols()"
                        :dataSource="site.invoices"
                        :loading="invoiceRefreshing"
                        :locale="{
                            emptyText: $t('billing.noInvoiceFound'),
                        }"
                        class="custom-table2"
                        rowKey="_id"
                        :pagination="false"
                        bordered
                    >
                        <div :key="'createdDateSlot'" :slot="'createdDateSlot'" slot-scope="data, item">
                            {{ shortDate(item.createdDate) }}
                        </div>
                        <div :key="'createdDateSlot'" :slot="'paymentDateSlot'" slot-scope="data, item">
                            {{ item.paymentDate | date }}
                        </div>
                        <div :key="'priceSlot'" :slot="'priceSlot'" slot-scope="data, item">
                            {{ item.price.toFixed(2).concat(site.basePrice.currency.toUpperCase()) }}
                        </div>
                        <div class="d-flex align-items-center" :slot="'paidSlot'" slot-scope="data, item">
                            <div
                                class="led mr-2"
                                :class="item.refund ? 'status-passive' : item.paid ? 'status-active' : 'status-passive'"
                            ></div>
                            <div>
                                {{
                                    item.refund
                                        ? "Refunded"
                                        : item.paid
                                            ? `${$t("billing.paid")}`
                                            : `${$t("billing.unPaid")}`
                                }}
                            </div>
                        </div>
                        <div :key="'actionsSlot' + $i18n.locale" :slot="'actionsSlot'" slot-scope="data, item">
                            <div class="d-flex w-100 justify-content-start">
                                <a-button
                                    :content="item.paid ? $t('billing.paid') : $t('billing.pay')"
                                    type="primary"
                                    @click="markPaid(item._id)"
                                    v-if="!item.paid && isAdmin && !item.refund"
                                >
                                    {{ $t("billing.markPaid") }}
                                </a-button>
                                <a-button
                                    :content="item.paid ? $t('billing.paid') : $t('billing.pay')"
                                    :disabled="item.paid || paying"
                                    class="actionButton d-flex align-items-center"
                                    @click="payBill(item._id)"
                                    v-if="!isAdmin"
                                >
                                    <span style="margin-right: 16px; display: block">{{
                                            item.paid ? $t("billing.paid") : $t("billing.pay")
                                        }}</span>
                                    <md-icon style="color: #40a9ff">payment</md-icon>
                                </a-button>
                                <a-button
                                    v-if="isAdmin"
                                    class="actionButton d-flex align-items-center"
                                    @click="delInvoice(item._id)"
                                >
                                    <ActionDelete />
                                </a-button>
                                <a-button
                                    v-if="isAdmin && item.paid && !item.refund"
                                    class="actionButton d-flex align-items-center"
                                    @click="refundInvoice(item._id)"
                                >
                                    Refund
                                </a-button>
                            </div>
                        </div>
                    </a-table>
                    <div class="mt-3 d-flex justify-content-end">
                        <Pagination :page-change="pageChange" :page-sizes="pageSizes" :total="site.invoices.length" />
                    </div>
                </div>
            </div>
        </div>
        <div v-else>
            <md-progress-spinner :md-diameter="20" class="md-accent" md-mode="indeterminate"></md-progress-spinner>
        </div>
    </div>
</template>
<script>
import { getSiteDetail } from "../../api/site";
import ActionDelete from "../svg/ActionDelete";
import ActionEdit from "../svg/ActionEdit";
import Pagination from "../Pagination";
import ShopifyIcon from "../fast-initialize/svg/ShopifyIcon";
import moment from "moment";
import _ from "lodash";
import { log2 } from "../../utils/log2";
import SYSTEM_PUBSUB from "../../utils/systemPubSub";
import { filter, map } from "rxjs/operators";
import { forceDeleteInvoice, markInvoiceAsPaid } from "../../api/billing";

const { takePayment, delInvoice, getInvoicesBySiteId, getInvoices, refundInvoice } = require("../../api/billing");
const { mapState } = require("vuex");
const log = log2("InvoiceList");

export default {
    name: "InvoiceList",
    components: {
        ShopifyIcon,
        ActionDelete,
        ActionEdit,
        Pagination,
    },
    props: {
        siteAddress: { type: String, required: true },
        initialData: { type: Object, required: false },
    },
    data() {
        return {
            site: null,
            loading: true,
            invoiceRefreshing: false,
            paying: false,
            pageSizes: ["10", "25", "50", "100"],
            perPage: 100,
            page: 1,
            cols: () => [
                {
                    dataIndex: "createdDate",
                    title: this.$t("billing.billingDate"),
                    scopedSlots: { customRender: "createdDateSlot" },
                    // ...tableSorter('name', 'string', this.currentSort),
                },
                {
                    dataIndex: "price",
                    title: this.$t("billing.price"),
                    scopedSlots: { customRender: "priceSlot" },
                },
                {
                    dataIndex: "paid",
                    title: this.$t("billing.isPaid"),
                    scopedSlots: { customRender: "paidSlot" },
                },
                {
                    dataIndex: "paymentDate",
                    title: this.$t("billing.paymentDate"),
                    scopedSlots: { customRender: "paymentDateSlot" },
                },
                {
                    title: this.$t("billing.transactions"),
                    scopedSlots: { customRender: "actionsSlot" },
                    width: "10%",
                },
            ],
        };
    },
    computed: {
        ...mapState({
            isAdmin: (state) => state.auth.adminUser != null,
            authSite: (state) => state.site.site,
        }),
    },
    mounted() {
        log("mounted", this.siteAddress);
        if (this.initialData) {
            this.site = this.initialData;
            if (!Array.isArray(this.site.invoices)) {
                this.site.invoices = [];
                this.fetchInvoices();
            }
            this.loading = false;
            this.invoiceRefreshing = false;
        } else {
            this.fetchData();
        }

        this.sub = SYSTEM_PUBSUB.stream
            .pipe(
                filter((x) => x.type === SYSTEM_PUBSUB.MESSAGE_TYPES.INVOICE_CREATED),
                map((x) => x.payload),
            )
            .subscribe((invoice) => {
                log("invoice create event received", invoice);
                this.fetchInvoices();
            });
    },
    beforeDestroy() {
        if (this.sub) this.sub.unsubscribe();
    },
    methods: {
        payBill(invoiceId) {
            log("payBill", invoiceId);
            this.paying = true;
            takePayment(invoiceId)
                .then((res) => {
                    const data = res.data;
                    if (data.status === "error") {
                        this.$toasted.error(data.message, {
                            duration: 3000,
                            position: "bottom-center",
                        });
                        return;
                    }

                    if (data.status !== "error") {
                        this.$toasted.success(this.$t("billing.paymentSuccess"), {
                            duration: 3000,
                            position: "bottom-center",
                        });
                        this.site.invoices = [
                            ...this.site.invoices.map((x) => {
                                if (x._id === invoiceId) {
                                    x.paid = true;
                                    x.paymentDate = Date.now();
                                }
                                return x;
                            }),
                        ];
                        const unpaidInvoiceCount = this.site.invoices.filter((x) => !x.paid).length;
                        if (unpaidInvoiceCount === 0 && this.site.suspend) {
                            window.location.reload();
                        }
                        this.fetchInvoices();
                    }
                })
                .catch((err) => {
                    console.error(err);
                    this.$toasted.error(this.$t("billing.paymentFail"), {
                        duration: 3000,
                        position: "bottom-center",
                    });
                })
                .finally(() => {
                    this.paying = false;
                });
        },
        async markPaid(invoiceId) {
            const confirmRes = await this.$dialog.confirm(this.$t("message.markPaidConfirm"), {
                okText: this.$t("message.continue"),
                cancelText: this.$t("message.cancel"),
            });
            log("confirmRes", confirmRes);
            markInvoiceAsPaid(this.site._id, invoiceId)
                .then((res) => res.data)
                .then((isSuccess) => {
                    if (isSuccess) {
                        this.$message.success({
                            content: this.$t("message.invoiceMarkedAsPaidSuccess"),
                            key: "mark-paid",
                            duration: 3,
                        });
                        this.site.invoices = this.site.invoices.map((invoice) => {
                            if (invoice._id === invoiceId) {
                                invoice.paid = true;
                                invoice.paymentDate = Date.now();
                            }
                            return invoice;
                        });
                    } else {
                        this.$message.error({
                            content: this.$t("message.invoiceMarkedAsPaidError"),
                            key: "mark-paid",
                            duration: 3,
                        });
                    }
                })
                .catch((err) => {
                    log.error("markInvoiceAsPaid error", err);
                    this.$message.error({
                        content: this.$t("message.invoiceMarkedAsPaidError"),
                        key: "mark-paid",
                        duration: 3,
                    });
                });
        },
        async delInvoice(invoiceId) {
            await this.$dialog.confirm(this.$t("message.deleteConfirm", { object: this.$t("billing.invoice") }), {
                okText: this.$t("message.continue"),
                cancelText: this.$t("message.cancel"),
            });
            if (this.isAdmin) await forceDeleteInvoice(this.site._id, invoiceId);
            else await delInvoice(invoiceId);
            this.site.invoices = this.site.invoices.filter((x) => x._id !== invoiceId);
        },
        async refundInvoice(invoiceId) {
            await this.$dialog.confirm(this.$t("message.refundInvoiceSure"), {
                okText: this.$t("message.continue"),
                cancelText: this.$t("message.cancel"),
            });
            await refundInvoice(invoiceId);
            this.site.invoices = this.site.invoices.map((x) => (x._id !== invoiceId ? x : { ...x, refund: true }));
        },
        shortDate(date) {
            return moment(date).format("YYYY-MM");
        },
        fetchData() {
            log("fetchData");
            if (this.isAdmin) {
                this.loading = true;
                getSiteDetail(this.siteAddress)
                    .then((response) => {
                        const site = response.data;
                        site.isIntegrated =
                            site.integratedOrigins.filter((x) => x.indexOf(".supsis.live") === -1).length > 0;
                        site.mobileUserCount = site.users.filter((x) => !!x.mobileNotification).length;
                        site.webUserCount = site.users.length - site.mobileUserCount;
                        this.site = site;
                        log("data fetched", site);
                    })
                    .catch((err) => {
                        log.error("site fetch error", err);
                        this.site = null;
                    })
                    .finally(() => {
                        this.loading = false;
                    });
            } else {
                this.site = this.authSite;
                this.site.invoices = [];
                this.loading = false;
                this.fetchInvoices();
            }
        },
        pageChange(page, pageSize) {
            log("pageChange", { page, pageSize });
            this.page = page;
            this.perPage = pageSize;
        },
        fetchInvoices() {
            log("fetchInvoices");

            this.invoiceRefreshing = true;
            const getInvoicesPromise = this.isAdmin
                ? getInvoicesBySiteId(this.site._id, this.perPage, this.page)
                : getInvoices(this.perPage, this.page);

            getInvoicesPromise
                .then((response) => {
                    this.site.invoices = response.data;
                })
                .catch((err) => {
                    console.error(err);
                })
                .finally(() => {
                    this.invoiceRefreshing = false;
                });
        },
        isShopify() {
            log("isShopify");
            return _.get(this.site, "partner.name") === "shopify";
        },
    },
    watch: {
        page(newValue, oldValue) {
            if (newValue) {
                this.fetchInvoices();
            }
        },
        perPage(newValue, oldValue) {
            if (newValue) {
                this.fetchInvoices();
            }
        },
    },
};
</script>
<style lang="scss">
#archive-billing {
    table {
        border: 1px solid #e8e8e8 !important;
        border-radius: 15px !important;
    }
}
</style>
<style lang="scss" scoped>
#archive-billing {
    .ant-table-bordered .ant-table-header > table,
    .ant-table-bordered .ant-table-body > table,
    .ant-table-bordered .ant-table-fixed-left table,
    .ant-table-bordered .ant-table-fixed-right table {
        border: 1px solid #e8e8e8 !important;
        border-radius: 15px !important;
    }

    .searching-input {
        width: 35%;
        border-radius: 30px;
    }

    .searching-icon {
        left: -10px !important;
    }

    .space-between-class {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        text-align: center;
        align-items: center;
    }

    .space-around-class {
        display: flex;
        flex-direction: row;
        justify-content: space-around;
        text-align: center;
        align-items: center;
    }

    .view-icon-btn {
        border: none;
        background: none;
    }

    .btn-wrapper-icon {
        height: 50px;
        border-radius: 15px !important;
    }

    .status-active {
        background: #6dd400;
        border-radius: 10px;
        padding: 2px;
        color: #ffffff;
        width: 60px;
    }

    .status-passive {
        background: #ec4b1d;
        border-radius: 10px;
        padding: 2px;
        color: #ffffff;
        width: 60px;
    }

    .led {
        height: 10px !important;
        width: 10px !important;
    }
}
</style>
